import { useState, useEffect, useContext, forwardRef, useImperativeHandle } from "react";
import ReactLoading from "react-loading";
import { OutorgaContext } from "../../../../../../contexts/Outorga";
import { getEquipamentosByMonitorado } from "../../../../../../services/equipamentos";
import { initializeParametersData, mapSavedParamsToState, buildPayload } from "../../../../../../utils/outorga/parametros/grantDefaultParamsMapper";
import { PARAMS_CONFIG } from "../../../../../../utils/outorga/parametros/grantDefaultParams";
import DefaultParamsRow from "./DefaultParamsRow";
import isEqual from "lodash/isEqual";

const DefaultParams = forwardRef((props, ref) => {
    const context = useContext(OutorgaContext);

    const [parametersData, setParametersData] = useState(initializeParametersData());
    const [equipmentList, setEquipmentList] = useState([]);
    const [isInitializing, setIsInitializing] = useState(true);
    const [lastSyncedParameters, setLastSyncedParameters] = useState(null);
    const [errors, setErrors] = useState({});

    const { getGrantDetails, getParameters, setUnsavedDefaultParams, isLoadingGrantDefaultParams } = context;

    const syncWithContext = (savedParams) => {
        if (!savedParams) return;

        const { updated } = mapSavedParamsToState(savedParams, {});
        setParametersData(updated);
        setLastSyncedParameters(savedParams);
    };

    useEffect(() => {
        const init = async () => {
            try {
                const idMonitorado = getGrantDetails()?.basicInfo?.monitorado;

                if (!idMonitorado) {
                    setIsInitializing(false);
                    return;
                }

                const resp = await getEquipamentosByMonitorado(idMonitorado);
                setEquipmentList(resp.equipamentos || []);

                syncWithContext(getParameters());
            } catch (err) {
                console.error(err);
            } finally {
                setIsInitializing(false);
            }
        };

        init();
    }, []);

    useEffect(() => {
        const currentParams = getParameters();

        if (!isEqual(currentParams, lastSyncedParameters)) {
            syncWithContext(currentParams);
        }
    });

    const updateParameter = (key, field, value) => {
        if (field === "isActive" && !value) {
            setErrors({});
        }

        if (field === "equipment") {
            setParametersData(prev => {
                const updated = {
                    ...prev,
                    [key]: {
                        ...prev[key],
                        equipment: value,
                        equipmentField: null
                    }
                };

                setErrors(prevErrors => ({
                    ...prevErrors,
                    [key]: { ...prevErrors?.[key], equipment: false }
                }));

                const payload = buildPayload(updated);
                setUnsavedDefaultParams(payload);

                return updated;
            });

            return;
        }

        setParametersData(prev => {
            const updated = {
                ...prev,
                [key]: {
                    ...prev[key],
                    [field]: value
                }
            };

            setErrors(prevErrors => ({
                ...prevErrors,
                [key]: { ...prevErrors[key], [field]: false }
            }));

            const payload = buildPayload(updated);
            setUnsavedDefaultParams(payload);

            return updated;
        });
    };

    const validateParameters = () => {
        const newErrors = {};

        PARAMS_CONFIG.forEach(param => {
            const data = parametersData[param.key];

            if (!data.isActive) return;

            newErrors[param.key] = {
                equipment: !data.equipment,
                equipmentField: !data.equipmentField,
                alertValueStart: data.alertValueStart === "",
                alertValueEnd: data.alertValueEnd === "",
                criticalValueStart: data.criticalValueStart === "",
                criticalValueEnd: data.criticalValueEnd === "",
                exceededValueStart: data.exceededValueStart === "",
                exceededValueEnd: data.exceededValueEnd === "",
            };
        });

        setErrors(newErrors);

        return {
            defaultIsValid: !Object.values(newErrors).some(paramErrors => Object.values(paramErrors).some(Boolean)),
        };
    };

    const handleSave = () => {
        return buildPayload(parametersData);
    };

    useImperativeHandle(ref, () => ({
        handleSave,
        validateParameters
    }));

    return (
        <div className="grant-default-params">
            {(isInitializing || isLoadingGrantDefaultParams) && (
                <div className="grant-default-params__loading">
                    <ReactLoading
                        type="spin"
                        color="var(--background-menu)"
                        width={40}
                    />
                </div>
            )}

            <div className="grant-default-params__body">
                {PARAMS_CONFIG.map(param => (
                    <DefaultParamsRow
                        key={param.key}
                        param={param}
                        data={parametersData[param.key]}
                        equipmentList={equipmentList}
                        error={errors[param.key]}
                        onChange={updateParameter}
                    />
                ))}
            </div>
        </div>
    );
});

export default DefaultParams;